import {useMutation, useQuery, useQueryClient} from 'react-query';
import toast from 'react-hot-toast';
import {useIntl} from 'react-intl';
import {checkIfAuthorized} from '../../../app/modules/auth/authorization/api';
import {usePermissions} from '../../../app/modules/auth/authorization/PermissionProvider';
import {ArpEntry, RouterCapabilities, RouterContext} from '../Types/type';
import {
  DmzSaveEntry,
  FirewallPrive,
  FirewallPublic,
  Lan,
  NatRule,
  PublicFirewallRule,
  Vpn,
  WlanInterface,
} from '../Types/routerForms';
import {
  addLease,
  addNatRule,
  addOption,
  addPrivateIp,
  addPublicRule,
  addVpnAccount,
  DhcpUpdatePayload,
  getArpTable,
  getFirewallPrive,
  getFirewallPublic,
  getLan,
  getRouterCapabilities,
  getRouterContext,
  getVpn,
  getWlan,
  moveNatRule,
  movePublicRule,
  removeLease,
  removeNatRule,
  removeOption,
  removePrivateIp,
  removePublicRule,
  removeVpnAccount,
  runPing,
  runTraceroute,
  saveDmz,
  setVpnSubnet,
  updateDhcp,
  updateL2tp,
  updateVpnPassword,
  updateWifi,
  VpnAccountPayload,
  WifiUpdatePayload,
} from '../Api/api';

const ROUTER_CONTEXT_QUERY_KEY = 'router-context';
const ROUTER_CAPABILITIES_QUERY_KEY = 'router-capabilities';
const ROUTER_ARP_QUERY_KEY = 'router-arp';
const ROUTER_LAN_QUERY_KEY = 'router-lan';
const ROUTER_WLAN_QUERY_KEY = 'router-wlan';
const ROUTER_VPN_QUERY_KEY = 'router-vpn';
const ROUTER_FW_PRIVE_QUERY_KEY = 'router-fw-prive';
const ROUTER_FW_PUBLIC_QUERY_KEY = 'router-fw-public';

// Récupère le message d'erreur renvoyé par le backend, sinon null.
export const getServerMessage = (error: any): string | null =>
  error?.response?.data?.message ?? null;

export const useGetRouterContext = (routerId: string | undefined) => {
  const intl = useIntl();
  return useQuery<RouterContext, unknown, RouterContext>(
    [ROUTER_CONTEXT_QUERY_KEY, routerId],
    () => getRouterContext(routerId!),
    {
      enabled: Boolean(routerId),
      onError: (error) => {
        toast.error(getServerMessage(error) ?? intl.formatMessage({id: 'CONFIGURATION.ROUTEUR.ERROR'}));
      },
    }
  );
};

/**
 * Capacités du routeur (ex : compatibilité de configuration), sans contacter le
 * matériel — utilisé en amont de la navigation vers la page de configuration
 * pour griser le bouton "Configurer" le cas échéant.
 */
export const useGetRouterCapabilities = (routerId: string | undefined) => {
  const userPermissions = usePermissions();
  const isAuthorized = checkIfAuthorized(userPermissions, ['voirConfiguration']);
  return useQuery<RouterCapabilities, unknown, RouterCapabilities>(
    [ROUTER_CAPABILITIES_QUERY_KEY, routerId],
    () => getRouterCapabilities(routerId!),
    {enabled: Boolean(routerId) && isAuthorized}
  );
};

export const useGetRouterArp = (routerId: string | undefined) =>
  useQuery<ArpEntry[], unknown, ArpEntry[]>(
    [ROUTER_ARP_QUERY_KEY, routerId],
    () => getArpTable(routerId!),
    {enabled: Boolean(routerId)}
  );

export const useRunPing = (routerId: string | undefined) => {
  const intl = useIntl();
  return useMutation({
    mutationFn: (destination: string) => runPing(routerId!, destination),
    onError: () => {
      toast.error(intl.formatMessage({id: 'CONFIGURATION.ROUTEUR.ERROR'}));
    },
  });
};

export const useRunTraceroute = (routerId: string | undefined) => {
  const intl = useIntl();
  return useMutation({
    mutationFn: (destination: string) => runTraceroute(routerId!, destination),
    onError: () => {
      toast.error(intl.formatMessage({id: 'CONFIGURATION.ROUTEUR.ERROR'}));
    },
  });
};

// --- LAN / DHCP ---

export const useGetLan = (routerId: string | undefined) =>
  useQuery<Lan, unknown, Lan>([ROUTER_LAN_QUERY_KEY, routerId], () => getLan(routerId!), {
    enabled: Boolean(routerId),
  });

/**
 * Fabrique de mutation LAN : invalide la query LAN au succès et affiche les
 * toasts succès/erreur. `mutationFn` reçoit les variables de la mutation.
 */
const useLanMutation = <TVariables>(
  routerId: string | undefined,
  mutationFn: (variables: TVariables) => Promise<void>
) => {
  const intl = useIntl();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn,
    onSuccess: () => {
      queryClient.invalidateQueries([ROUTER_LAN_QUERY_KEY, routerId]);
      toast.success(intl.formatMessage({id: 'CONFIGURATION.ROUTEUR.SUCCESS'}));
    },
    onError: () => {
      toast.error(intl.formatMessage({id: 'CONFIGURATION.ROUTEUR.ERROR'}));
    },
  });
};

export const useAddPrivateIp = (routerId: string | undefined) =>
  useLanMutation(routerId, (payload: {address: string; mask: string}) =>
    addPrivateIp(routerId!, payload)
  );

export const useRemovePrivateIp = (routerId: string | undefined) =>
  useLanMutation(routerId, (id: string) => removePrivateIp(routerId!, id));

export const useUpdateDhcp = (routerId: string | undefined) =>
  useLanMutation(routerId, (payload: DhcpUpdatePayload) => updateDhcp(routerId!, payload));

export const useAddLease = (routerId: string | undefined) =>
  useLanMutation(routerId, (payload: {ip: string; mac: string}) => addLease(routerId!, payload));

export const useRemoveLease = (routerId: string | undefined) =>
  useLanMutation(routerId, (id: string) => removeLease(routerId!, id));

export const useAddOption = (routerId: string | undefined) =>
  useLanMutation(routerId, (payload: {name: string; code: string; value: string}) =>
    addOption(routerId!, payload)
  );

export const useRemoveOption = (routerId: string | undefined) =>
  useLanMutation(routerId, (id: string) => removeOption(routerId!, id));

// --- WLAN ---

export const useGetWlan = (routerId: string | undefined) =>
  useQuery<WlanInterface[], unknown, WlanInterface[]>(
    [ROUTER_WLAN_QUERY_KEY, routerId],
    () => getWlan(routerId!),
    {enabled: Boolean(routerId)}
  );

export const useUpdateWifi = (routerId: string | undefined) => {
  const intl = useIntl();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: WifiUpdatePayload) => updateWifi(routerId!, payload),
    onSuccess: () => {
      queryClient.invalidateQueries([ROUTER_WLAN_QUERY_KEY, routerId]);
      toast.success(intl.formatMessage({id: 'CONFIGURATION.ROUTEUR.SUCCESS'}));
    },
    onError: () => {
      toast.error(intl.formatMessage({id: 'CONFIGURATION.ROUTEUR.ERROR'}));
    },
  });
};

// --- VPN L2TP ---

export const useGetVpn = (routerId: string | undefined) =>
  useQuery<Vpn, unknown, Vpn>([ROUTER_VPN_QUERY_KEY, routerId], () => getVpn(routerId!), {
    enabled: Boolean(routerId),
  });

/** Fabrique de mutation VPN : invalide la query VPN + toasts succès/erreur. */
const useVpnMutation = <TVariables>(
  routerId: string | undefined,
  mutationFn: (variables: TVariables) => Promise<void>
) => {
  const intl = useIntl();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn,
    onSuccess: () => {
      queryClient.invalidateQueries([ROUTER_VPN_QUERY_KEY, routerId]);
      toast.success(intl.formatMessage({id: 'CONFIGURATION.ROUTEUR.SUCCESS'}));
    },
    onError: () => {
      toast.error(intl.formatMessage({id: 'CONFIGURATION.ROUTEUR.ERROR'}));
    },
  });
};

export const useUpdateL2tp = (routerId: string | undefined) =>
  useVpnMutation(routerId, (payload: {active: boolean; ipsecSecret: string}) =>
    updateL2tp(routerId!, payload)
  );

export const useSetVpnSubnet = (routerId: string | undefined) =>
  useVpnMutation(routerId, (subnet: string) => setVpnSubnet(routerId!, subnet));

export const useAddVpnAccount = (routerId: string | undefined) =>
  useVpnMutation(routerId, (payload: VpnAccountPayload) => addVpnAccount(routerId!, payload));

export const useRemoveVpnAccount = (routerId: string | undefined) =>
  useVpnMutation(routerId, (id: string) => removeVpnAccount(routerId!, id));

export const useUpdateVpnPassword = (routerId: string | undefined) =>
  useVpnMutation(routerId, (payload: {id: string; password: string}) =>
    updateVpnPassword(routerId!, payload.id, payload.password)
  );

// --- Firewall ---

export const useGetFirewallPrive = (routerId: string | undefined) =>
  useQuery<FirewallPrive, unknown, FirewallPrive>(
    [ROUTER_FW_PRIVE_QUERY_KEY, routerId],
    () => getFirewallPrive(routerId!),
    {enabled: Boolean(routerId)}
  );

export const useGetFirewallPublic = (routerId: string | undefined) =>
  useQuery<FirewallPublic, unknown, FirewallPublic>(
    [ROUTER_FW_PUBLIC_QUERY_KEY, routerId],
    () => getFirewallPublic(routerId!),
    {enabled: Boolean(routerId)}
  );

const useFirewallMutation = <TVariables>(
  routerId: string | undefined,
  queryKey: string,
  mutationFn: (variables: TVariables) => Promise<void>
) => {
  const intl = useIntl();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn,
    onSuccess: () => {
      queryClient.invalidateQueries([queryKey, routerId]);
      toast.success(intl.formatMessage({id: 'CONFIGURATION.ROUTEUR.SUCCESS'}));
    },
    onError: () => {
      toast.error(intl.formatMessage({id: 'CONFIGURATION.ROUTEUR.ERROR'}));
    },
  });
};

export const useAddNatRule = (routerId: string | undefined) =>
  useFirewallMutation(routerId, ROUTER_FW_PRIVE_QUERY_KEY, (payload: Omit<NatRule, 'id'>) =>
    addNatRule(routerId!, payload)
  );

export const useRemoveNatRule = (routerId: string | undefined) =>
  useFirewallMutation(routerId, ROUTER_FW_PRIVE_QUERY_KEY, (id: string) =>
    removeNatRule(routerId!, id)
  );

export const useMoveNatRule = (routerId: string | undefined) =>
  useFirewallMutation(routerId, ROUTER_FW_PRIVE_QUERY_KEY, (id: string) =>
    moveNatRule(routerId!, id)
  );

export const useSaveDmz = (routerId: string | undefined) =>
  useFirewallMutation(routerId, ROUTER_FW_PRIVE_QUERY_KEY, (entries: DmzSaveEntry[]) =>
    saveDmz(routerId!, entries)
  );

export const useAddPublicRule = (routerId: string | undefined) =>
  useFirewallMutation(
    routerId,
    ROUTER_FW_PUBLIC_QUERY_KEY,
    (payload: Omit<PublicFirewallRule, 'id'>) => addPublicRule(routerId!, payload)
  );

export const useRemovePublicRule = (routerId: string | undefined) =>
  useFirewallMutation(routerId, ROUTER_FW_PUBLIC_QUERY_KEY, (id: string) =>
    removePublicRule(routerId!, id)
  );

export const useMovePublicRule = (routerId: string | undefined) =>
  useFirewallMutation(routerId, ROUTER_FW_PUBLIC_QUERY_KEY, (id: string) =>
    movePublicRule(routerId!, id)
  );
